import React, { useCallback, useEffect, useState, forwardRef } from 'react';

interface VideoRecord {
  mediaRecordRef: React.MutableRefObject<MediaRecorder | null>;
  setIsRecordReady: React.Dispatch<React.SetStateAction<boolean>>;
  setVideoRecordDataWebm: React.Dispatch<
    React.SetStateAction<Blob | undefined>
  >;
  cameraWidth: number;
  setRecordReplaySrc: React.Dispatch<React.SetStateAction<string | null>>;
}

 
const videoConstraints = {
  width: { ideal: 360 },
  height: { ideal: 640 },
  aspectRatio: 16 / 9,
  frameRate: 30,
  facingMode: "user"
}

const VideoRecord = forwardRef<HTMLVideoElement, VideoRecord>(({
  mediaRecordRef,
  setIsRecordReady,
  setVideoRecordDataWebm,
  cameraWidth,
  setRecordReplaySrc,
}, ref) => {
  const [videoPreview, setVideoPreview] = useState<HTMLVideoElement | null>(
    null
  );

  const refCallback = (el: HTMLVideoElement | null) => {
    setVideoPreview(el);

    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el;
    }
  };

  const handleClearCameraConnection = useCallback(async () => {
    if (videoPreview && videoPreview.srcObject) {
      const stream = videoPreview.srcObject as MediaStream;
      const tracks = stream.getTracks();
      for (let i = 0; i < tracks.length; i++) {
        tracks[i].stop();
        tracks[i].enabled = false;
      }
      videoPreview.srcObject = null;
      videoPreview.src = '';
      const videoPreviewEl = document.getElementById(
        'video-preview'
      ) as HTMLVideoElement;
      if (videoPreviewEl) {
        videoPreviewEl.srcObject = null;
        videoPreviewEl.src = '';
      }
      setVideoPreview(null);
    }

    if (mediaRecordRef.current) {
      console.info('Clearing media record');
      mediaRecordRef.current.ondataavailable = null;
      mediaRecordRef.current.stop();
      mediaRecordRef.current = null;
    }
  }, [videoPreview, mediaRecordRef]);

  useEffect(() => {
    const fn = async () => {
      if (!videoPreview) {
        return;
      }

      const bitrate = Math.round(0.2 * 1024 * 1024);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: cameraWidth, frameRate: videoConstraints.frameRate },
          // video: videoConstraints,
          audio: true,
        });

        videoPreview.srcObject = stream;

        try {
          mediaRecordRef.current = new MediaRecorder(stream, {
            videoBitsPerSecond: bitrate,
            mimeType: 'video/mp4',
          });

          /** alert('support: ' + MediaRecorder.isTypeSupported('video/mp4')); */
        } catch (e) {
          console.error(
            'MediaRecorder with specified options failed, falling back to default:',
            e
          );
          mediaRecordRef.current = new MediaRecorder(stream); // Fallback to default
        }
        mediaRecordRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            setIsRecordReady(true);

            const recordWebm = new Blob([event.data], {
              type: 'video/webm',
            });
            setVideoRecordDataWebm(recordWebm);

            const recordReplayBlob = new Blob([event.data], {
              type: 'video/mp4',
            });
            const url = window.webkitURL.createObjectURL(recordReplayBlob);
            setRecordReplaySrc(url);
          }
        };
      } catch (e) {
        console.error('Video camera is not available', e);
        alert(
          'Vidoe camera is not available. Please provide permissions and refresh the page'
        );
      }
    };
    fn();

    return () => {
      handleClearCameraConnection();
    };
  }, [
    cameraWidth,
    handleClearCameraConnection,
    mediaRecordRef,
    setIsRecordReady,
    setRecordReplaySrc,
    setVideoRecordDataWebm,
    videoPreview,
  ]);

  return (
    <video
      id="video-preview"
      playsInline
      muted
      autoPlay={true}
      style={{
        position: 'relative',
        zIndex: 5,
        width: '100%'
      }}
      ref={refCallback}
    ></video>
  );
});

export default VideoRecord;
